import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import BottomNavigation from '../components/BottomNavigation';
import { useAuthState } from 'react-firebase-hooks/auth';

// --- Options for Multi-Select ---
const concernOptions = ['جوش', 'لک', 'چروک', 'حساسیت', 'خشکی', 'منافذ', 'کدری', 'هایپرپیگمنتیشن'];
const iranianBrandsOptions = [
  'سینره', 'شون', 'نئودرم', 'مای', 'سی گل', 'درماسیف', 'پریم', 'هیدرودرم', 'سایر'
];
const internationalBrandsOptions = [
  'CeraVe', 'The Ordinary', 'La Roche-Posay', 'Vichy', 'Bioderma', 'Neutrogena', "Paula's Choice", 'L\'Oréal', 'COSRX', 'Eucerin', 'سایر'
];
// --- End Options ---

const Profile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableUserData, setEditableUserData] = useState(null);
  const [saving, setSaving] = useState(false);

  const [user, authLoading] = useAuthState(auth);

  useEffect(() => {
    // Don't fetch if auth is loading or user is not available
    if (authLoading || !user) {
      setLoading(false);
      return; 
    }

    const fetchUserData = async () => {
      setLoading(true); // Start loading when fetching
      try {
        const docRef = doc(db, "users", user.uid); // Use user.uid directly
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const fetchedData = docSnap.data();
          setUserData(fetchedData);
          // Ensure arrays/objects exist in editable data
          setEditableUserData({
            ...fetchedData,
            concerns: fetchedData.concerns || [],
            knownBrands: {
              iranian: fetchedData.knownBrands?.iranian || [],
              international: fetchedData.knownBrands?.international || [],
            },
            // Ensure lifestyle object exists
            lifestyle: fetchedData.lifestyle || {},
          });
        } else {
          console.log("No profile document found!");
          setUserData(null);
          // Initialize with empty structure for editing
          setEditableUserData({
            concerns: [],
            knownBrands: { iranian: [], international: [] },
            lifestyle: {},
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserData(null); // Clear data on error
        setEditableUserData(null);
      } finally {
        setLoading(false); // Stop loading regardless of outcome
      }
    };

    fetchUserData();
    // Depend on user object (or user.uid) and authLoading state
  }, [user, authLoading]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleEditToggle = () => {
    if (!isEditing) {
      // Entering edit mode, ensure editable data is synced with current user data
      setEditableUserData(userData || {});
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableUserData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle nested changes for lifestyle
  const handleLifestyleChange = (e) => {
    const { name, value } = e.target;
    setEditableUserData(prevData => ({
      ...prevData,
      lifestyle: {
        ...(prevData.lifestyle || {}),
        [name]: value,
      },
    }));
  };

  const handleDeleteConcern = (indexToDelete) => {
    setEditableUserData(prevData => ({
      ...prevData,
      concerns: (prevData.concerns || []).filter((_, index) => index !== indexToDelete),
    }));
  };

  const handleDeleteBrand = (type, indexToDelete) => {
    setEditableUserData(prevData => {
      const updatedBrands = { ...(prevData.knownBrands || { iranian: [], international: [] }) };
      updatedBrands[type] = (updatedBrands[type] || []).filter((_, index) => index !== indexToDelete);
       return {
        ...prevData,
        knownBrands: updatedBrands,
      };
    });
  };

  const handleToggleSelection = (item, type) => {
    setEditableUserData(prevData => {
      let currentSelection = [];
      let updatedData = { ...prevData };

      if (type === 'concerns') {
        currentSelection = prevData.concerns || [];
        const isSelected = currentSelection.includes(item);
        updatedData.concerns = isSelected 
          ? currentSelection.filter(c => c !== item)
          : [...currentSelection, item];
      } else {
        const brandType = type;
        const updatedBrands = { ...(prevData.knownBrands || { iranian: [], international: [] }) };
        currentSelection = updatedBrands[brandType] || [];
        const isSelected = currentSelection.includes(item);
        updatedBrands[brandType] = isSelected
          ? currentSelection.filter(b => b !== item)
          : [...currentSelection, item];
        updatedData.knownBrands = updatedBrands;
      }

      return updatedData;
    });
  };

  const handleSaveProfile = async () => {
    if (!user) return; // Should not happen if logged in
    setSaving(true);
    try {
      const docRef = doc(db, "users", user.uid);
      await updateDoc(docRef, editableUserData);
      setUserData(editableUserData); // Update display data
      setIsEditing(false); // Exit edit mode
      console.log("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      // Optionally show an error message to the user
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen text-black font-light pb-20" dir="rtl">
      <div className="max-w-md mx-auto pt-6 px-4">
        {/* Header with Menu - Simplified */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">پروفایل</h1>
          {/* Edit/Save/Cancel Buttons - Moved directly under the flex container */}
          {/* Keep buttons grouped */}
          <div className="flex space-x-2 rtl:space-x-reverse">
            {isEditing ? (
              <>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className={`px-4 py-1 rounded-full text-sm font-semibold ${saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} text-white transition-colors duration-200`}
                >
                  {saving ? 'ذخیره‌سازی...' : 'ذخیره'}
                </button>
                <button
                  onClick={handleEditToggle} // Also used for Cancel
                  disabled={saving}
                  className="px-4 py-1 rounded-full text-sm font-semibold bg-gray-200 hover:bg-gray-300 text-black transition-colors duration-200"
                >
                  لغو
                </button>
              </>
            ) : (
              <button
                onClick={handleEditToggle}
                className="px-4 py-1 rounded-full text-sm font-semibold bg-blue-500 hover:bg-blue-600 text-white transition-colors duration-200"
              >
                ویرایش
              </button>
            )}
          </div>
        </div>

        {/* Profile Content */}
        {editableUserData ? ( // Check if editableUserData exists before rendering inputs
          <div className="space-y-6">
            {/* --- Personal Information --- */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">اطلاعات شخصی</h2>
              <div className="space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">نام کامل</label>
                  {isEditing ? (
                    <input 
                      type="text"
                      name="fullName"
                      value={editableUserData.fullName || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    />
                  ) : (
                    <p className="text-gray-800">{userData?.fullName || '-'}</p>
                  )}
                </div>
                {/* Age */}
                 <div>
                  <label className="block text-sm text-gray-600 mb-1">سن</label>
                  {isEditing ? (
                    <input 
                      type="number"
                      name="age"
                      value={editableUserData.age || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    />
                  ) : (
                    <p className="text-gray-800">{userData?.age || '-'}</p>
                  )}
                </div>
                {/* Gender */}
                 <div>
                  <label className="block text-sm text-gray-600 mb-1">جنسیت</label>
                  {isEditing ? (
                    <select
                      name="gender"
                      value={editableUserData.gender || ''}
                      onChange={handleInputChange}
                       className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white"
                    >
                      <option value="">انتخاب کنید</option>
                      <option value="مرد">مرد</option>
                      <option value="زن">زن</option>
                      <option value="دیگر">دیگر</option>
                    </select>
                  ) : (
                    <p className="text-gray-800">{userData?.gender || '-'}</p>
                  )}
                </div>
                {/* City */}
                 <div>
                  <label className="block text-sm text-gray-600 mb-1">شهر</label>
                  {isEditing ? (
                    <input 
                      type="text"
                      name="city"
                      value={editableUserData.city || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    />
                  ) : (
                    <p className="text-gray-800">{userData?.city || '-'}</p>
                  )}
                </div>
                 {/* Job */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">شغل</label>
                  {isEditing ? (
                    <input 
                      type="text"
                      name="job"
                      value={editableUserData.job || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    />
                  ) : (
                    <p className="text-gray-800">{userData?.job || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* --- Skin Information --- */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">اطلاعات پوست</h2>
              <div className="space-y-4">
                 {/* Skin Type */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">نوع پوست</label>
                  {isEditing ? (
                    <select 
                      name="skinType"
                      value={editableUserData.skinType || ''}
                      onChange={handleInputChange}
                       className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white"
                    >
                      <option value="">انتخاب کنید</option>
                      <option value="چرب">چرب</option>
                      <option value="خشک">خشک</option>
                      <option value="مختلط">مختلط</option>
                      <option value="نرمال">نرمال</option>
                      <option value="حساس">حساس</option>
                    </select>
                  ) : (
                    <p className="text-gray-800">{userData?.skinType || '-'}</p>
                  )}
                </div>
                {/* Skin Tone */}
                 <div>
                  <label className="block text-sm text-gray-600 mb-1">رنگ پوست</label>
                  {isEditing ? (
                     <select
                      name="skinTone"
                      value={editableUserData.skinTone || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white"
                     >
                      <option value="">انتخاب کنید</option>
                      <option value="بسیار روشن">بسیار روشن</option>
                      <option value="روشن">روشن</option>
                      <option value="گندمی روشن">گندمی روشن</option>
                      <option value="گندمی تیره">گندمی تیره</option>
                      <option value="سبزه">سبزه</option>
                      <option value="تیره">تیره</option>
                     </select>
                  ) : (
                    <p className="text-gray-800">{userData?.skinTone || '-'}</p>
                  )}
                </div>
                {/* Concerns */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">مشکلات پوستی</label>
                  {isEditing ? (
                    <div className="flex flex-wrap gap-2">
                      {concernOptions.map((concern) => {
                        const isSelected = editableUserData.concerns?.includes(concern);
                        return (
                          <button
                            key={concern}
                            type="button"
                            onClick={() => handleToggleSelection(concern, 'concerns')}
                            className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${isSelected ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                          >
                            {concern}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                     <div className="flex flex-wrap gap-2">
                        {userData?.concerns?.length > 0 ? (
                          userData.concerns.map((concern, index) => (
                            <span key={index} className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">{concern}</span>
                          ))
                        ) : (
                          <p className="text-gray-500">وارد نشده</p>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* --- Lifestyle Information --- */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">سبک زندگی</h2>
              <div className="space-y-4">
                 {/* Exercise */}
                 <div>
                    <label className="block text-sm text-gray-600 mb-1">ورزش می‌کنید؟</label>
                    {isEditing ? (
                      <select name="exercise" value={editableUserData.lifestyle?.exercise || ''} onChange={handleLifestyleChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
                        <option value="">انتخاب کنید</option>
                        <option value="بله">بله</option>
                        <option value="خیر">خیر</option>
                      </select>
                    ) : (
                      <p className="text-gray-800">{userData?.lifestyle?.exercise || '-'}</p>
                    )}
                  </div>
                 {/* Smoking */}
                 <div>
                    <label className="block text-sm text-gray-600 mb-1">سیگار می‌کشید؟</label>
                    {isEditing ? (
                       <select name="smoking" value={editableUserData.lifestyle?.smoking || ''} onChange={handleLifestyleChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
                        <option value="">انتخاب کنید</option>
                        <option value="بله">بله</option>
                        <option value="خیر">خیر</option>
                      </select>
                    ) : (
                      <p className="text-gray-800">{userData?.lifestyle?.smoking || '-'}</p>
                    )}
                  </div>
                 {/* Stress */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">سطح استرس معمول شما؟</label>
                    {isEditing ? (
                      <select name="stress" value={editableUserData.lifestyle?.stress || ''} onChange={handleLifestyleChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
                        <option value="">انتخاب کنید</option>
                        <option value="کم">کم</option>
                        <option value="متوسط">متوسط</option>
                        <option value="زیاد">زیاد</option>
                      </select>
                    ) : (
                      <p className="text-gray-800">{userData?.lifestyle?.stress || '-'}</p>
                    )}
                  </div>
                 {/* Sleep */}
                 <div>
                    <label className="block text-sm text-gray-600 mb-1">کیفیت خواب شما؟</label>
                     {isEditing ? (
                       <select name="sleep" value={editableUserData.lifestyle?.sleep || ''} onChange={handleLifestyleChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
                        <option value="">انتخاب کنید</option>
                        <option value="خوب">خوب</option>
                        <option value="متوسط">متوسط</option>
                        <option value="ضعیف">ضعیف</option>
                      </select>
                    ) : (
                      <p className="text-gray-800">{userData?.lifestyle?.sleep || '-'}</p>
                    )}
                  </div>
                 {/* Doctor */}
                 <div>
                    <label className="block text-sm text-gray-600 mb-1">به متخصص پوست مراجعه کرده‌اید؟</label>
                    {isEditing ? (
                      <select name="doctor" value={editableUserData.lifestyle?.doctor || ''} onChange={handleLifestyleChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
                        <option value="">انتخاب کنید</option>
                        <option value="بله">بله</option>
                        <option value="خیر">خیر</option>
                      </select>
                    ) : (
                      <p className="text-gray-800">{userData?.lifestyle?.doctor || '-'}</p>
                    )}
                  </div>
                 {/* Diet */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">رژیم غذایی سالم دارید؟</label>
                    {isEditing ? (
                       <select name="diet" value={editableUserData.lifestyle?.diet || ''} onChange={handleLifestyleChange} className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white">
                        <option value="">انتخاب کنید</option>
                        <option value="بله">بله</option>
                        <option value="تاحدودی">تاحدودی</option>
                        <option value="خیر">خیر</option>
                      </select>
                    ) : (
                      <p className="text-gray-800">{userData?.lifestyle?.diet || '-'}</p>
                    )}
                  </div>
              </div>
            </div>

            {/* --- Known Brands --- */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">برندهای شناخته شده</h2>
              <div className="space-y-4">
                {/* Iranian Brands */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">برندهای ایرانی</label>
                   {isEditing ? (
                    <div className="flex flex-wrap gap-2">
                      {iranianBrandsOptions.map((brand) => {
                        const isSelected = editableUserData.knownBrands?.iranian?.includes(brand);
                         return (
                          <button
                            key={brand}
                            type="button"
                            onClick={() => handleToggleSelection(brand, 'iranian')}
                             className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${isSelected ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                          >
                            {brand}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {userData?.knownBrands?.iranian?.length > 0 ? (
                        userData.knownBrands.iranian.map((brand, index) => (
                           <span key={index} className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">{brand}</span>
                        ))
                      ) : (
                        <p className="text-gray-500">وارد نشده</p>
                      )}
                    </div>
                  )}
                </div>
                 {/* International Brands */}
                <div>
                  <label className="block text-sm text-gray-600 mb-1">برندهای خارجی</label>
                   {isEditing ? (
                    <div className="flex flex-wrap gap-2">
                      {internationalBrandsOptions.map((brand) => {
                         const isSelected = editableUserData.knownBrands?.international?.includes(brand);
                         return (
                          <button
                            key={brand}
                            type="button"
                            onClick={() => handleToggleSelection(brand, 'international')}
                             className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${isSelected ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                          >
                            {brand}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {userData?.knownBrands?.international?.length > 0 ? (
                        userData.knownBrands.international.map((brand, index) => (
                           <span key={index} className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">{brand}</span>
                        ))
                      ) : (
                        <p className="text-gray-500">وارد نشده</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-10">اطلاعات پروفایل در دسترس نیست.</div>
        )}
      </div>
    </div>
  );
};

export default Profile; 